import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Monitor, Printer, Package, Wrench, Network, Cctv, MapPin, MessageCircle,
  Phone, Store, Building2, Hospital, Landmark, GraduationCap, Star, FolderKanban,
  ShieldCheck, SunMedium,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { BrandImage } from "@/components/BrandImage";
import { Marquee } from "@/components/Marquee";
import { PodcastCard } from "@/components/PodcastCard";
import { SectionHead, CardsGrid, SplitFeature, StepsRow, CtaBand, splitRows, CARD_MAX_W } from "@/components/blocks";
import { useQuote } from "@/components/QuoteModal";
import { COMPANY, IMAGES, wa, WA_MESSAGES } from "@/data/site";
import { track } from "@/lib/analytics";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const MARQUEE_ITEMS = [
  "ASSEMBLED PCS", "PRINTERS & TONERS", "NETWORKING", "CCTV INSTALLATION", "UPS & POWER",
  "MALLAPPALLY • PATHANAMTHITTA • KERALA",
];

const HERO_LINES = ["Reliable Technology Solutions", "for Homes, Businesses & Institutions"];

const TRUST_ITEMS = [
  { icon: Monitor, label: "Assembled PCs", to: "/products/assembled-computers" },
  { icon: Printer, label: "Printers, Toner & Ink", to: "/products/printers" },
  { icon: Network, label: "Networking", to: "/products/networking" },
  { icon: Cctv, label: "CCTV Installation", to: "/services/cctv-installation" },
  { icon: MapPin, label: "Local On-Site Support", to: "/services" },
];

const SERVICES = [
  { icon: Monitor, title: "Assembled PCs", text: "Custom-built desktop PCs configured according to your requirements and budget.", cta: "Explore Computers", to: "/products/assembled-computers" },
  { icon: Printer, title: "Printer Solutions", text: "Printers, installation, configuration and related support for home and business users.", cta: "Explore Printers", to: "/products/printers" },
  { icon: Package, title: "Toners & Consumables", text: "Printer toner, ink and consumables for your everyday printing requirements.", cta: "Check Availability", to: "/products/toners" },
  { icon: Wrench, title: "Computer & Laptop Service", text: "Troubleshooting, repair, installation and technical assistance.", cta: "Book a Service", to: "/services/computer-service" },
  { icon: Network, title: "Networking Services", text: "Network equipment, installation, configuration and support.", cta: "Explore Networking", to: "/products/networking" },
  { icon: Cctv, title: "CCTV Installation", text: "CCTV supply and installation for homes, businesses and institutions.", cta: "Explore CCTV", to: "/products/cctv" },
];

const BUSINESS_CARDS = [
  { icon: Store, title: "Small Business", to: "/business-solutions/small-business" },
  { icon: Building2, title: "Offices", to: "/business-solutions/offices" },
  { icon: Hospital, title: "Hospitals & Clinics", to: "/business-solutions/hospitals" },
  { icon: Landmark, title: "Banks & Financial Offices", to: "/business-solutions/banks" },
  { icon: GraduationCap, title: "Institutions", to: "/business-solutions" },
];

const WHY_CTS = [
  { title: "Practical Recommendations", text: "We focus on what you actually need rather than simply selling the most expensive option." },
  { title: "Complete Solutions", text: "From products to installation and technical support, we can help with multiple technology requirements." },
  { title: "Local Technical Support", text: "When you need hands-on assistance, our local service team can provide on-site support within our service area." },
  { title: "Business Experience", text: "We have experience serving individuals as well as businesses, hospitals, private-sector banks, financial offices and other institutions." },
  { title: "Ongoing Relationship", text: "Our relationship doesn't have to end when the product is sold. We can continue to help with installation, configuration, repairs and technical support when you need us." },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: COMPANY.name,
  url: "https://cherianstechsolutions.com/",
  alternateName: COMPANY.short,
  description: "Reliable technology solutions for homes, businesses and institutions — assembled PCs, printers, toners, networking, CCTV, installation and technical support in Mallappally, Kerala.",
  telephone: "+91 62380 98067",
  email: COMPANY.email,
  foundingDate: "2019",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Panickamuryil & Melekuttu Plaza, Central Junction, Mallappally West",
    addressLocality: "Mallappally",
    addressRegion: "Kerala",
    postalCode: "689585",
    addressCountry: "IN",
  },
  areaServed: ["Mallappally", "Pathanamthitta", "Kerala"],
};

const BizCard = ({ card, i }) => (
  <Reveal delay={i * 60}>
    <Link
      to={card.to}
      data-testid={`business-card-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="block h-full rounded-lg bg-[#3A444B] border border-white/10 p-6 transition-all duration-200 hover:-translate-y-1 hover:border-cts-red"
    >
      <card.icon size={26} strokeWidth={1.75} className="text-cts-red mb-4" />
      <h3 className="font-heading font-bold text-white text-[15px] leading-snug">{card.title}</h3>
    </Link>
  </Reveal>
);

export default function Home() {
  const { openQuote } = useQuote();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const [mainBizCards, lastBizRow] = splitRows(BUSINESS_CARDS, 3);
  const [podcasts, setPodcasts] = useState([]);

  useEffect(() => {
    api.get("/podcasts", { params: { limit: 3 } })
      .then(({ data }) => setPodcasts((data.items || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <>
      <Seo
        title="Cherians Tech Solutions | Computers, Printers, Networking & CCTV in Mallappally"
        siteName={COMPANY.brand}
        description="Reliable technology solutions for homes, businesses and institutions — assembled PCs, printers, toners, networking, CCTV installation and local technical support from CTS Mallappally."
        image="/cts-logo.png"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative bg-white overflow-hidden" ref={heroRef} data-testid="hero-section">
        <div className="absolute left-0 top-0 h-full w-1.5 bg-cts-red" aria-hidden="true" />
        <div className="container-site section-pad grid gap-12 lg:grid-cols-2 items-center relative">
          <div>
            <motion.p
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="eyebrow mb-4 font-mono text-[12px] tracking-[0.25em]"
              data-testid="hero-eyebrow"
            >
              TECHNOLOGY. SOLUTIONS. SUPPORT.
            </motion.p>
            <h1 className="h1-display" data-testid="hero-heading">
              {HERO_LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "100%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.85, delay: 0.15 * i, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="lead-text mt-6 max-w-xl"
            >
              From assembled computers and printers to networking, CCTV, installation and technical
              support, Cherians Tech Solutions helps you choose, install and maintain the technology
              you depend on.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.68, ease: EASE }}
              className="flex flex-col sm:flex-row gap-4 mt-8"
            >
              <button onClick={() => openQuote()} data-testid="hero-quote-btn" className="btn-primary">
                GET A QUOTE
              </button>
              <a
                href={wa(WA_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { location: "hero" })}
                data-testid="hero-whatsapp-btn"
                className="btn-whatsapp"
              >
                <MessageCircle size={18} /> WHATSAPP US
              </a>
              <a
                href={COMPANY.phoneHref}
                onClick={() => track("phone_click", { location: "hero" })}
                data-testid="hero-call-btn"
                className="btn-outline"
              >
                <Phone size={17} /> CALL NOW
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-6 flex items-center gap-2 text-sm text-cts-charcoal"
            >
              <MapPin size={15} className="text-cts-red" />
              Mallappally, Pathanamthitta — on-site support within 15–20 km
            </motion.p>
          </div>
          <div className="relative">
            <motion.div
              style={{ y: heroImgY }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
              className="relative rounded-xl overflow-hidden shadow-2xl"
            >
              <BrandImage
                src={IMAGES.heroPc}
                imgKey="heroPc"
                alt="Custom-assembled desktop computer built and configured"
                eager
                testId="hero-image"
                className="relative w-full h-80 md:h-[440px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-cts-ink/35 via-cts-ink/0 to-transparent pointer-events-none" aria-hidden="true" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <Marquee items={MARQUEE_ITEMS} />

      {/* TRUST STRIP */}
      <section className="bg-cts-soft border-y border-cts-line" data-testid="trust-strip">
        <div className="container-site py-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {TRUST_ITEMS.map((item, i) => (
            <Reveal key={item.label} delay={i * 60}>
              <Link
                to={item.to}
                data-testid={`trust-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex items-center gap-3 group"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-cts-line shrink-0 transition-colors group-hover:border-cts-red">
                  <item.icon size={20} strokeWidth={1.75} className="text-cts-red" />
                </span>
                <span className="font-heading font-bold text-[12.5px] uppercase tracking-wide text-cts-charcoal leading-snug transition-colors group-hover:text-cts-red">
                  {item.label}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="section-pad-tight bg-white">
        <div className="container-site max-w-3xl">
          <SectionHead eyebrow="ABOUT CTS" title="Technology That Works for You" />
          <Reveal>
            <div className="space-y-4 lead-text">
              <p>Technology should make your work easier — not create more problems.</p>
              <p>
                At Cherians Tech Solutions, we help customers select the right technology, set it up
                properly and get the technical support they need when problems arise.
              </p>
              <p>
                Whether you need an assembled PC for your home or office, a printer and toner,
                networking for your business, or CCTV installation, our goal is simple: provide a
                practical solution that fits your requirement and budget.
              </p>
            </div>
            <Link to="/contact" data-testid="intro-talk-btn" className="btn-outline mt-8">
              TALK TO CTS
            </Link>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 rounded-xl border border-cts-line bg-cts-soft p-7" data-testid="credibility-band">
              <span className="font-heading font-extrabold text-cts-red text-5xl leading-none">12+</span>
              <div>
                <h3 className="font-heading font-bold text-cts-ink text-lg">Years of Business Experience</h3>
                <p className="text-[15px] text-cts-charcoal mt-1 leading-relaxed">
                  Serving customers since 2014, with CTS operating under its current name since 2019.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="section-pad bg-cts-soft" data-testid="home-services">
        <div className="container-site">
          <SectionHead eyebrow="OUR SERVICES" title="Technology Solutions That Work" />
          <CardsGrid
            items={SERVICES.map((s) => ({ icon: s.icon, title: s.title, text: s.text, cta: s.cta, to: s.to }))}
          />
        </div>
      </section>

      {/* BUSINESS SOLUTIONS */}
      <section className="section-pad bg-cts-charcoal" data-testid="home-business">
        <div className="container-site">
          <SectionHead
            light
            eyebrow="BUSINESS SOLUTIONS"
            title="Technology That Works for Your Business"
            text="Your business doesn't just need computers. It needs technology that works together reliably. CTS can help with computers, printers, networking, CCTV, installation and ongoing technical support."
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {mainBizCards.map((card, i) => (
              <BizCard key={card.title} card={card} i={i} />
            ))}
          </div>
          {lastBizRow.length > 0 && (
            <div className="flex flex-wrap justify-center gap-5 mt-5">
              {lastBizRow.map((card, i) => (
                <div key={card.title} className="w-full sm:w-[calc(50%-10px)]" style={{ maxWidth: CARD_MAX_W[3] }}>
                  <BizCard card={card} i={mainBizCards.length + i} />
                </div>
              ))}
            </div>
          )}
          <Reveal className="mt-10 text-center">
            <button onClick={() => openQuote("Business IT Requirements")} data-testid="business-discuss-btn" className="btn-primary">
              DISCUSS YOUR BUSINESS REQUIREMENT
            </button>
          </Reveal>
        </div>
      </section>

      {/* WHY CTS — MANIFESTO */}
      <section className="section-pad bg-white" data-testid="home-why">
        <div className="container-site max-w-4xl">
          <SectionHead eyebrow="WHY CTS" title="Why Choose Cherians Tech Solutions?" />
          <div className="divide-y divide-cts-line">
            {WHY_CTS.map((item, i) => (
              <Reveal key={item.title} delay={i * 70}>
                <div className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 py-7 md:py-8 group">
                  <span className="font-mono font-bold text-cts-red/25 text-5xl md:text-6xl leading-none tracking-tight transition-colors duration-300 group-hover:text-cts-red/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-cts-ink text-lg md:text-xl mb-2 uppercase tracking-tight">{item.title}</h3>
                    <p className="text-[15px] text-cts-charcoal leading-relaxed max-w-xl">{item.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ASSEMBLED PC FEATURE */}
      <section className="section-pad bg-cts-soft" data-testid="home-pc-feature">
        <div className="container-site">
          <SplitFeature
            eyebrow="ASSEMBLED COMPUTERS"
            title="A Computer Built Around What You Actually Need"
            paragraphs={[
              "We assemble desktop computers based on your requirements, applications and budget — whether it's everyday home use, office work, accounting, business applications or more demanding workloads.",
            ]}
            image={IMAGES.workspace}
            imgKey="workspace"
            alt="Assembled desktop computer built by CTS"
            testId="pc-feature"
          >
            <StepsRow steps={["Tell us what you do", "We recommend a configuration", "We assemble", "We test and configure"]} />
            <Link to="/products/assembled-computers#enquiry" data-testid="pc-feature-cta" className="btn-primary mt-7">
              BUILD MY PC
            </Link>
          </SplitFeature>
        </div>
      </section>

      {/* PRINTER / TONER FEATURE */}
      <section className="section-pad bg-white" data-testid="home-printer-feature">
        <div className="container-site">
          <SplitFeature
            reverse
            eyebrow="PRINTERS & TONERS"
            title="Your Printer Doesn't End With the Purchase"
            paragraphs={["We can help with printer selection, installation, toner and ink requirements."]}
            image={IMAGES.printer}
            imgKey="printer"
            alt="Printer supplied and installed by CTS"
            testId="printer-feature"
          >
            <StepsRow steps={["Choose", "Install", "Keep Printing"]} />
            <div className="flex flex-col sm:flex-row gap-4 mt-7">
              <Link to="/products/toners#enquiry" data-testid="toner-feature-cta" className="btn-primary">
                CHECK TONER &amp; INK AVAILABILITY
              </Link>
              <a
                href={wa(WA_MESSAGES.toner)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { location: "toner_feature" })}
                data-testid="toner-whatsapp-btn"
                className="btn-outline"
              >
                <MessageCircle size={17} /> WHATSAPP YOUR MODEL
              </a>
            </div>
          </SplitFeature>
        </div>
      </section>

      {/* NETWORKING FEATURE */}
      <section className="section-pad bg-cts-soft" data-testid="home-network-feature">
        <div className="container-site">
          <SplitFeature
            eyebrow="NETWORKING"
            title="Better Connectivity for Your Business"
            paragraphs={["From network equipment to installation and configuration, CTS helps businesses build reliable connectivity."]}
            image={IMAGES.network}
            imgKey="network"
            alt="Business networking installed by CTS"
            testId="network-feature"
          >
            <Link to="/services/networking" data-testid="network-feature-cta" className="btn-primary">
              DISCUSS YOUR NETWORK REQUIREMENT
            </Link>
          </SplitFeature>
        </div>
      </section>

      {/* CCTV FEATURE */}
      <section className="section-pad bg-white" data-testid="home-cctv-feature">
        <div className="container-site">
          <SplitFeature
            reverse
            eyebrow="CCTV & SURVEILLANCE"
            title="CCTV Planned Around Your Property"
            paragraphs={[
              "We supply and install CCTV systems for homes, businesses and institutions — planned around what you actually need to see, from camera placement to recording setup and testing.",
            ]}
            image={IMAGES.cctv}
            imgKey="cctv"
            alt="CCTV camera installed on a building exterior"
            testId="cctv-feature"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => openQuote("CCTV")} data-testid="cctv-feature-cta" className="btn-primary">
                REQUEST A CCTV QUOTE
              </button>
              <Link to="/products/cctv" data-testid="cctv-feature-explore" className="btn-outline">
                EXPLORE CCTV
              </Link>
            </div>
          </SplitFeature>
        </div>
      </section>

      {/* FUTURE SERVICES */}
      <section className="section-pad bg-cts-soft" data-testid="home-future">
        <div className="container-site">
          <SectionHead eyebrow="PLANNING FOR THE FUTURE" title="What CTS Is Preparing Next" />
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
            <Reveal>
              <div className="card-base h-full p-7" data-testid="future-amc-card">
                <div className="flex items-start justify-between mb-4">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-cts-redlight">
                    <ShieldCheck size={24} strokeWidth={1.75} className="text-cts-red" />
                  </span>
                  <span className="font-heading font-bold text-[10px] tracking-[0.14em] uppercase bg-cts-red text-white rounded px-2.5 py-1">COMING SOON</span>
                </div>
                <h3 className="font-heading font-bold text-cts-ink text-lg mb-2 uppercase">AMC Services</h3>
                <p className="text-[15px] text-cts-charcoal leading-relaxed">
                  Annual Maintenance Contracts for businesses and institutions that want dependable
                  ongoing technology support.
                </p>
                <Link to="/amc#register" data-testid="future-amc-cta" className="link-arrow mt-4">REGISTER YOUR INTEREST</Link>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="card-base h-full p-7" data-testid="future-solar-card">
                <div className="flex items-start justify-between mb-4">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-cts-redlight">
                    <SunMedium size={24} strokeWidth={1.75} className="text-cts-red" />
                  </span>
                  <span className="font-heading font-bold text-[10px] tracking-[0.14em] uppercase bg-cts-red text-white rounded px-2.5 py-1">COMING SOON</span>
                </div>
                <h3 className="font-heading font-bold text-cts-ink text-lg mb-2 uppercase">Solar Energy Solutions</h3>
                <p className="text-[15px] text-cts-charcoal leading-relaxed">
                  CTS is preparing to expand into solar energy solutions for homes and businesses.
                </p>
                <Link to="/services/solar#register" data-testid="future-solar-cta" className="link-arrow mt-4">REGISTER YOUR INTEREST</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section-pad bg-white" data-testid="home-projects">
        <div className="container-site">
          <SectionHead
            eyebrow="OUR WORK"
            title="Real Installations. Real Requirements. Practical Solutions."
          />
          <Reveal>
            <div className="rounded-xl border border-cts-line bg-cts-soft p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-white border border-cts-line shrink-0">
                <FolderKanban size={26} strokeWidth={1.75} className="text-cts-red" />
              </span>
              <div className="flex-1">
                <h3 className="h3-display mb-2">Case Studies Are Being Documented</h3>
                <p className="text-[15px] text-cts-charcoal leading-relaxed">
                  We're documenting real CTS installations — with the customer's permission and genuine
                  photographs. Each case study will cover the requirement, challenge, solution and result.
                </p>
              </div>
              <Link to="/projects" data-testid="projects-view-btn" className="btn-outline shrink-0">VIEW PROJECTS</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-pad bg-cts-soft" data-testid="home-reviews">
        <div className="container-site max-w-3xl text-center">
          <SectionHead center eyebrow="CUSTOMER REVIEWS" title="What Our Customers Say" />
          <Reveal>
            <div className="rounded-xl border border-cts-line bg-white p-10" data-testid="reviews-panel">
              <Star size={32} strokeWidth={1.5} className="text-cts-red mx-auto mb-4" />
              <h3 className="font-heading font-bold text-cts-ink text-lg">Your Experience Matters</h3>
              <p className="text-[15px] text-cts-charcoal mt-2 max-w-md mx-auto leading-relaxed">
                We value honest feedback from our customers. Genuine CTS reviews are available on
                Google — so you can always read them at the source.
              </p>
              <a
                href={COMPANY.googleSearch}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leave-review-btn"
                className="btn-primary mt-6"
              >
                LEAVE A GOOGLE REVIEW
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PODCAST */}
      <section className="section-pad bg-white" data-testid="home-podcast">
        <div className="container-site">
          <SectionHead
            eyebrow="CTS BUSINESS INSIGHTS"
            title="The CTS Podcast"
            text="Practical technology conversations from Cherians Tech Solutions — in English, Malayalam and Hindi."
          />
          {podcasts.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {podcasts.map((e, i) => <PodcastCard key={e.id} episode={e} i={i} />)}
            </div>
          ) : (
            <div className="rounded-xl border border-cts-line bg-cts-soft p-8 max-w-2xl" data-testid="home-podcast-empty">
              <p className="text-[15px] leading-relaxed text-cts-charcoal">
                New episodes of CTS Business Insights are coming soon.
              </p>
            </div>
          )}
          <Reveal className="mt-8">
            <Link to="/podcasts" data-testid="home-podcast-explore-btn" className="btn-outline">
              EXPLORE ALL PODCASTS
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <CtaBand />
    </>
  );
}
